import { addClassNamesToElement } from '@lexical/utils'
import {
  $applyNodeReplacement,
  $createTextNode,
  ElementNode,
  TextNode,
  type DOMConversionMap,
  type EditorConfig,
  type LexicalNode,
  type NodeKey
} from 'lexical'

import { isEmpty } from '@eastcoal/common/utils/object'

import linkDefaultIcon from '../../images/linkDefaultIcon.png'

import type {
  LinkNodeOptions,
  LinkNodeProps,
  LinkNodeState,
  SerializedLinkNode
} from './interface'

const createImageDom = (anchor: HTMLAnchorElement, icon: string) => {
  const img = document.createElement('img')
  img.style.height = '1em'
  img.src = icon
  img.onerror = () => {
    img.src = linkDefaultIcon
  }
  return img
}

export class LinkNode extends ElementNode {
  private __data: LinkNodeState
  private __manualInsert: boolean

  constructor({ icon, url, manualInsert }: LinkNodeProps, key?: NodeKey) {
    super(key)
    this.__data = { icon, url }
    this.__manualInsert = manualInsert ?? false
  }

  isManualInsert() {
    return this.__manualInsert
  }

  getTextContent() {
    return super.getTextContent() || this.getState().url || '链接'
  }

  setTextContent(text?: string) {
    const content = text || this.getState().url || '链接'
    let textNode = this.getChildren()[0] as TextNode | undefined
    if (!textNode) {
      textNode = $createTextNode()
      this.append(textNode)
    }
    textNode.setTextContent(content)
  }

  getState() {
    return this.__data
  }

  setState(state: Partial<LinkNodeState>) {
    const writable = this.getWritable()
    writable.__data = {
      // 注意：外部传递参数时一定要确保 __data 里面没有 children，不然会导致 children 被反复合并
      ...writable.__data,
      ...state
    }
  }

  static getType(): string {
    return 'link'
  }

  static clone(node: LinkNode) {
    return new LinkNode(node.getState(), node.getKey())
  }

  exportJSON(): SerializedLinkNode {
    return {
      ...super.exportJSON(),
      ...this.__data,
      type: LinkNode.getType()
    }
  }

  static importJSON(serializedNode: SerializedLinkNode): LinkNode {
    const node = new LinkNode({
      url: serializedNode.url,
      icon: serializedNode.icon
    })

    node.setFormat(serializedNode.format)
    node.setIndent(serializedNode.indent)
    node.setDirection(serializedNode.direction)

    return node
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: () => ({
        priority: 0,
        conversion(dom) {
          if (
            !(dom instanceof HTMLAnchorElement) ||
            isEmpty(dom.textContent?.trim())
          ) {
            return null
          }
          const url = dom.href
          const icon = dom.querySelector('img')?.src
          const linkNode = $createLinkNode({ url, icon })
          linkNode.setTextContent(dom.textContent)
          return {
            node: linkNode
          }
        }
      })
    }
  }

  createDOM(config: EditorConfig): HTMLAnchorElement {
    const { url, icon } = this.getState()
    const anchor = document.createElement('a')
    anchor.target = '_blank'
    anchor.rel = 'noopener'
    if (url) {
      anchor.href = url
    }
    if (icon) {
      const img = createImageDom(anchor, icon)
      anchor.append(img)
    }
    addClassNamesToElement(anchor, config.theme.link)
    return anchor
  }

  updateDOM(
    prevNode: LinkNode,
    anchor: HTMLAnchorElement,
    config: EditorConfig
  ): boolean {
    const { url, icon } = this.getState()
    const { url: prevUrl, icon: prevIcon } = prevNode.getState()
    if (url !== prevUrl) {
      anchor.href = url ?? ''
    }
    if (icon !== prevIcon) {
      anchor.querySelector('img')?.remove()
      if (icon) {
        const img = createImageDom(anchor, icon)
        anchor.prepend(img)
      }
    }

    return false
  }

  isInline(): boolean {
    return true
  }

  canInsertTextBefore(): boolean {
    return false
  }

  canInsertTextAfter(): boolean {
    return false
  }
}

export const $isLinkNode = (node: LexicalNode | null): node is LinkNode => {
  return node instanceof LinkNode
}

export const $createLinkNode = (options: LinkNodeOptions): LinkNode => {
  const linkNode: LinkNode = $applyNodeReplacement(
    new LinkNode({
      ...options,
      manualInsert: true
    })
  )
  linkNode.setTextContent(options.text)
  return linkNode
}
